import { Box, Grid, InputAdornment, Typography } from "@mui/material";
import { OLPaper } from "OLPaper";
import { DayMonthInput } from "components/FormControls/DayMonthInput/DayMonthInput";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { useFormikContext } from "formik";
import { useMessage } from "hooks/intl";
import { LimitsSubSection } from "./LimitsSubSection";
import { useStyles } from "./WalletType.styles";

interface Props {
  isNegativeBalanceAlreadySet?: boolean;
}

export function UnitSettings({ isNegativeBalanceAlreadySet }: Props) {
  const classes = useStyles();
  const { message } = useMessage();
  const { values, setFieldValue } = useFormikContext<any>();

  return (
    <>
      <OLPaper className={classes.OLPaper}>
        <Grid container spacing={10}>
          <Grid item xs>
            <TextInput
              className={classes.fullWidth}
              data-test-id="singular-name-input"
              label={
                <RequiredFieldLabel
                  text={message({
                    id: "unitSingularName",
                  })}
                />
              }
              name="unitSingularName"
            />
          </Grid>
          <Grid item xs>
            <TextInput
              className={classes.fullWidth}
              data-test-id="plural-input"
              label={
                <RequiredFieldLabel text={message({ id: "unitPluralName" })} />
              }
              name="unitPluralName"
            />
          </Grid>
        </Grid>
        <Box mt={12} />
        <Grid container spacing={10}>
          <Grid xs item>
            <SelectInput
              className={classes.SelectInput}
              data-test-id="expiration-method-select"
              items={[
                {
                  id: "all_time_active",
                  label: message({ id: "noExpiration" }),
                },
                {
                  id: "after_x_days",
                  label: message({ id: "afterXDays" }),
                },
                {
                  id: "at_the_end_of_the_month",
                  label: message({ id: "atTheEndOfTheMonth" }),
                },
                {
                  id: "at_the_end_of_the_x_th_year",
                  label: message({ id: "atTheEndOfTheXthYear" }),
                },
                {
                  id: "at_the_end_of_the_chosen_date",
                  label: message({ id: "annualExpirationOnChosenDate" }),
                },
              ]}
              label={
                <RequiredFieldLabel
                  text={message({ id: "unitExpirationMethod" })}
                />
              }
              name="unitDaysExpiryAfter"
              onChange={(_e, newValue: string) => {
                switch (newValue) {
                  case "all_time_active":
                  case "at_the_end_of_the_month":
                    setFieldValue("unitDaysActiveCount", undefined);
                    setFieldValue("unitYearsActiveCount", undefined);
                    setFieldValue("unitExpiryDate", undefined);
                    break;
                  case "after_x_days":
                    setFieldValue("unitYearsActiveCount", undefined);
                    setFieldValue("unitExpiryDate", undefined);
                    break;
                  case "at_the_end_of_the_x_th_year":
                    setFieldValue("unitDaysActiveCount", undefined);
                    setFieldValue("unitExpiryDate", undefined);
                    break;
                  case "at_the_end_of_the_chosen_date":
                    setFieldValue("unitDaysActiveCount", undefined);
                    setFieldValue("unitYearsActiveCount", undefined);
                }
              }}
            />
          </Grid>
          {values.unitDaysExpiryAfter === "after_x_days" && (
            <Grid xs item>
              <NumberFormatInput
                name="unitDaysActiveCount"
                data-test-id="expiration-period-input"
                label={
                  <RequiredFieldLabel
                    text={message({ id: "unitExpirationPeriod" })}
                  />
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({ id: "days" }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
                width="100%"
              />
            </Grid>
          )}
          {values.unitDaysExpiryAfter === "at_the_end_of_the_x_th_year" && (
            <Grid xs item>
              <NumberFormatInput
                name="unitYearsActiveCount"
                data-test-id="expiration-value-input"
                helperText={message({
                  id: "unitsYearsActiveCountHelperText",
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({ id: "years" }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
                label={
                  <RequiredFieldLabel
                    text={message({ id: "unitExpirationPeriod" })}
                  />
                }
                width="100%"
              />
            </Grid>
          )}
          {values.unitDaysExpiryAfter === "at_the_end_of_the_chosen_date" && (
            <>
              <Grid xs item>
                <DayMonthInput
                  name="unitExpiryDate"
                  label={message({ id: "date" })}
                />
              </Grid>
              <div className={classes.annualExpirationDescWrapper}>
                <Typography className={classes.annualExpirationDesc}>
                  {message({ id: "annualExpirationOnChosenDateExplanation" })}
                </Typography>
              </div>
            </>
          )}
        </Grid>
        <Box mt={12} />
        <Grid container spacing={10}>
          <Grid xs item>
            <SelectInput
              className={classes.SelectInput}
              data-test-id="unit-pending-method-select"
              items={[
                {
                  id: "true",
                  label: message({ id: "noPending" }),
                },
                {
                  id: "false",
                  label: message({ id: "pendingForXDays" }),
                },
              ]}
              label={
                <RequiredFieldLabel
                  text={message({ id: "unitPendingMethod" })}
                />
              }
              name="allTimeNotLocked"
              onChange={(_e, newValue: string) => {
                if (newValue === "true") {
                  return setFieldValue("unitDaysLocked", undefined);
                }
              }}
            />
          </Grid>
          {values.allTimeNotLocked === "false" && (
            <Grid xs item>
              <NumberFormatInput
                name="unitDaysLocked"
                data-test-id="unit-pending-period-input"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {message({ id: "days" }).toLowerCase()}
                    </InputAdornment>
                  ),
                }}
                label={
                  <RequiredFieldLabel
                    text={message({ id: "unitPendingPeriod" })}
                  />
                }
                width="100%"
              />
            </Grid>
          )}
        </Grid>
        <Box mt={12} />
        <LimitsSubSection pathPrefix="points" data-test-id="limits-global" />
        <Box mt={12} />
        <LimitsSubSection
          pathPrefix="pointsPerMember"
          data-test-id="limits-member"
        />
        <>
          <div className={classes.separator} />
          <Grid xs item alignItems="center" display="flex" gap={4}>
            <AlertTriangleIcon className={classes.warnIcon} />
            <Typography>
              {message({ id: "activeUnitsBecomeNegative" })}
            </Typography>
          </Grid>
          <Grid item xs className={classes.toggleWrapper}>
            <Toggle
              name="allowNegativeBalance"
              label={message({ id: "enableNegativeUnitssBalance" })}
              disabled={isNegativeBalanceAlreadySet}
            />
          </Grid>
          <Typography className={classes.negativeDesc}>
            {message(
              { id: "negativeBalanceInfo" },
              {
                warning: <strong>{message({ id: "warning" })}:</strong>,
                learnMore: "",
                // TODO Enable when we will have a link to docs
                // (
                //   <ExternalLink path="" className={classes.link}>
                //     {message({ id: "learnMore" })}
                //   </ExternalLink>
                // ),
              },
            )}
          </Typography>
        </>
      </OLPaper>
    </>
  );
}
